import View from '@play-co/timestep-core/lib/ui/View';
import PopupBasicLazy from 'src/game/components/popups/PopupBasicLazy';
import { ButtonView, ImageScaleView, animate } from '@play-co/timestep-core/ui';
import { AssetGroup } from '@play-co/timestep-core';
import { Feature } from 'src/replicant/analytics';
import { waitForItPromise } from 'src/lib/utils';
import { downscale } from 'src/conf/scale';
import StateObserver from 'src/StateObserver';
import sounds from 'src/lib/sounds';

type CloseHandler = (ok: boolean) => void;

export default class PopupCardsInfo extends PopupBasicLazy {
  constructor(opts: { superview: View; close: CloseHandler }) {
    const width = downscale(1080);
    const height = downscale(1920);

    super({
      superview: opts.superview,
      skipBackground: true,
      skipTitle: true,
      width,
      height,
      close: () => opts.close(true),
      tracking: {
        feature: Feature.CARDS._,
        $subFeature: Feature.CARDS.POPUP_INFO,
      },
      zIndex: 10000,
    });

    this.buttonClose.hide();

    this.bg.updateOpts({
      backgroundColor: 'rgba(71,24,67,0.9)',
    });

    new ButtonView({
      superview: this.box,
      width,
      height,
      zIndex: 1,
      onClick: () => {
        sounds.sfx.play('ok');
        opts.close(true);
      },
    });
  }

  public getAssetsGroup() {
    return new AssetGroup([
      ...AssetGroup.constructURLs([
        'assets/ui/cards/common',
        'assets/ui/cards/popupCardsInfo',
      ]),
    ]);
  }

  public async init() {
    super.init({});
  }

  public async fadeIn() {
    await super.fadeIn();
    this.animateIn();
  }

  private async animateIn() {
    await waitForItPromise(300);

    sounds.sfx.play('win');

    const locale = StateObserver.getLocale();
    const en = locale === 'en';
    const cx = this.box.style.width * 0.5;

    // Title
    const title = new ImageScaleView({
      superview: this.box,
      image: `assets/ui/cards/common/title_${locale}.png`,
      width: downscale(813),
      height: downscale(186),
      scaleMethod: 'contain',
      x: cx,
      y: 100,
      centerOnOrigin: true,
    });
    await this.showAnimated(title);

    // How to Play
    const howToPlay = new ImageScaleView({
      superview: this.box,
      image: `assets/ui/cards/popupCardsInfo/how_to_play_${locale}.png`,
      width: downscale(en ? 288 : 136),
      height: downscale(en ? 61 : 58),
      scaleMethod: 'contain',
      x: cx,
      y: 205,
      centerOnOrigin: true,
    });
    await this.showAnimated(howToPlay);

    // Item 1 + Text 1 (left)
    const item1 = new ImageScaleView({
      superview: this.box,
      image: 'assets/ui/cards/popupCardsInfo/item_1.png',
      width: downscale(321),
      height: downscale(272),
      scaleMethod: 'contain',
      x: 200,
      y: 330,
      centerOnOrigin: true,
    });
    new ImageScaleView({
      superview: item1,
      image: `assets/ui/cards/popupCardsInfo/text_1_${locale}.png`,
      width: downscale(en ? 220 : 207),
      height: downscale(en ? 47 : 51),
      scaleMethod: 'contain',
      x: item1.style.width * 0.5 - 20,
      y: item1.style.height * 0.5 + 120,
      centerOnOrigin: true,
    });
    await this.showAnimated(item1);

    // Arrow 1
    const arrow1 = new ImageScaleView({
      superview: this.box,
      image: 'assets/ui/cards/popupCardsInfo/arrow.png',
      width: downscale(129),
      height: downscale(129),
      scaleMethod: 'contain',
      x: cx - 20,
      y: 450,
      centerOnOrigin: true,
    });
    await this.showAnimated(arrow1);

    // Item 2 + Text 2 (right)
    const item2 = new ImageScaleView({
      superview: this.box,
      image: 'assets/ui/cards/popupCardsInfo/item_2.png',
      width: downscale(434),
      height: downscale(319),
      scaleMethod: 'contain',
      x: 530,
      y: 470,
      centerOnOrigin: true,
    });
    new ImageScaleView({
      superview: item2,
      image: `assets/ui/cards/popupCardsInfo/text_2_${locale}.png`,
      width: downscale(en ? 283 : 346),
      height: downscale(en ? 103 : 98),
      scaleMethod: 'contain',
      x: item2.style.width * 0.5,
      y: item2.style.height * 0.5 + 140,
      centerOnOrigin: true,
    });
    await this.showAnimated(item2);

    // Arrow 2 (flipped)
    const arrow2 = new ImageScaleView({
      superview: this.box,
      image: 'assets/ui/cards/popupCardsInfo/arrow.png',
      width: downscale(129),
      height: downscale(129),
      scaleMethod: 'contain',
      flipX: true,
      x: cx,
      y: 650,
      centerOnOrigin: true,
    });
    await this.showAnimated(arrow2);

    // Item 3 + Text 3 (left)
    const item3 = new ImageScaleView({
      superview: this.box,
      image: 'assets/ui/cards/popupCardsInfo/item_3.png',
      width: downscale(509),
      height: downscale(303),
      scaleMethod: 'contain',
      x: 200,
      y: 800,
      centerOnOrigin: true,
    });
    new ImageScaleView({
      superview: item3,
      image: `assets/ui/cards/popupCardsInfo/text_3_${locale}.png`,
      width: downscale(en ? 420 : 314),
      height: downscale(en ? 94 : 98),
      scaleMethod: 'contain',
      x: item3.style.width * 0.5,
      y: item3.style.height * 0.5 + 140,
      centerOnOrigin: true,
    });
    await this.showAnimated(item3);

    // Info
    const info = new ImageScaleView({
      superview: this.box,
      image: `assets/ui/cards/popupCardsInfo/info_${locale}.png`,
      width: downscale(945),
      height: downscale(173),
      scaleMethod: 'contain',
      x: cx,
      y: 1080,
      centerOnOrigin: true,
    });
    await this.showAnimated(info);

    // Tap to Continue
    const tapToContinue = new ImageScaleView({
      superview: this.box,
      image: `assets/ui/cards/popupCardsInfo/tap_to_continue_${locale}.png`,
      width: downscale(en ? 569 : 433),
      height: downscale(en ? 87 : 84),
      scaleMethod: 'contain',
      x: cx,
      y: 1210,
      centerOnOrigin: true,
    });
    await this.showAnimated(tapToContinue);
  }

  private async showAnimated(view: View) {
    view.updateOpts({ scale: 0 });
    animate(view).then({ scale: 1 }, 300, animate.easeOutBack);
    sounds.sfx.play('puzzle-ice-tap');
    await waitForItPromise(200);
  }
}
